import { useEffect, useMemo, useState } from 'react';

import { useLocalStorageState } from '@components/component-helpers';
import { useScopedTranslate } from '@locale/translate';

export const fuzzyMatch = (text: string, pattern: string): boolean => {
  if (!pattern) return true;
  if (!text) return false;

  const textLower = text.toLowerCase();
  const patternLower = pattern.toLowerCase();

  if (textLower.includes(patternLower)) {
    return true;
  }

  const words = textLower.split(/[\s\-_.]+/);
  return words.some((word) => word.startsWith(patternLower));
};

type SearchAndCollapseOptions<T> = {
  data: T[];
  getGroupKey: (item: T) => string;
  searchFilter: (item: T, searchStr: string) => boolean;
  localStorageKey?: string;
};

export const useSearchAndCollapse = <T>({
  data,
  getGroupKey,
  searchFilter,
  localStorageKey,
}: SearchAndCollapseOptions<T>) => {
  const { t } = useScopedTranslate(locale);

  const [searchStr, setSearchStr] = useState('');

  const default_state = false;

  const filteredData = useMemo(() => {
    if (!searchStr.trim()) {
      return data;
    }

    return data.filter((item) => searchFilter(item, searchStr.toLowerCase()));
  }, [data, searchStr, searchFilter]);

  const [collapsedState, setCollapsedState] = useLocalStorageState<Record<string, boolean>>({
    key: localStorageKey ? `collapse-state-${localStorageKey}` : null,
    defaultValue: {},
  });

  const isAllCollapsed = useMemo(() => {
    if (filteredData.length === 0) {
      return true;
    }

    return filteredData.every((item) => {
      const key = getGroupKey(item);
      return collapsedState[key] ?? default_state;
    });
  }, [collapsedState, default_state, filteredData, getGroupKey]);

  useEffect(() => {
    if (filteredData.length > 0) {
      setCollapsedState((prevState) => {
        const newState = { ...prevState };
        let hasChanges = false;

        filteredData.forEach((item) => {
          const key = getGroupKey(item);
          if (!(key in newState)) {
            newState[key] = false;
            hasChanges = true;
          }
        });

        return hasChanges ? newState : prevState;
      });
    }
  }, [filteredData, getGroupKey, setCollapsedState]);

  const getCollapsedState = (key: string): boolean => {
    return collapsedState[key] ?? default_state;
  };

  const collapsedStateWithDefaults = useMemo(() => {
    const proxy = new Proxy(collapsedState, {
      get(target, prop) {
        if (typeof prop === 'string') {
          return target[prop] ?? default_state;
        }
        return target[prop as unknown as keyof typeof target];
      },
    });
    return proxy;
  }, [collapsedState, default_state]);

  const expandCollapseText = isAllCollapsed ? t('expandAll') : t('collapseAll');

  const handleExpandCollapse = () => {
    const newCollapsedState = !isAllCollapsed;

    const updatedState = filteredData.reduce(
      (acc, item) => {
        acc[getGroupKey(item)] = newCollapsedState;
        return acc;
      },
      {} as Record<string, boolean>,
    );

    setCollapsedState(updatedState);
  };

  const toggleCollapse = (key: string) => {
    setCollapsedState((prev) => ({
      ...prev,
      [key]: !getCollapsedState(key),
    }));
  };

  return {
    searchStr,
    setSearchStr,
    filteredData,
    collapsedState: collapsedStateWithDefaults,
    isAllCollapsed,
    expandCollapseText,
    handleExpandCollapse,
    toggleCollapse,
  };
};

const locale = {
  en: {
    expandAll: 'Expand all',
    collapseAll: 'Collapse all',
  },
};
